import React from "react";
import { useTranslation } from "react-i18next";
import ModalShell from "../ModalShell";
import {
  PiReceiptLight,
  PiDownloadSimpleLight,
  PiArrowSquareOutLight,
} from "react-icons/pi";
import { Invoice } from "../../../store/actions/PlanActions";
import Spinner from '../TailwindComponents/Spinner';

interface InvoicesModalProps {
  open: boolean;
  onClose: () => void;
  invoices: Invoice[];
  loading?: boolean;
}

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    paid: "bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-400 border-success-200 dark:border-success-800",
    open: "bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-400 border-info-200 dark:border-info-800",
    draft: "bg-surface-sunken/70 text-fg-muted border-hairline",
    void: "bg-surface-sunken/70 text-fg-muted border-hairline",
    uncollectible:
      "bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-400 border-danger-200 dark:border-danger-800",
  };
  return map[status] || map.draft;
};

const formatCurrency = (amount: number, currency: string) => {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (timestamp: number) => {
  return new Date(timestamp * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const InvoicesModal: React.FC<InvoicesModalProps> = ({
  open,
  onClose,
  invoices,
  loading = false,
}) => {
  const { t } = useTranslation();

  return (
    <ModalShell
      isOpen={open}
      onClose={onClose}
      title={t("Invoices")}
      icon={<PiReceiptLight />}
      size="lg"
      bodyClassName="overflow-y-auto"
      footer={
        invoices.length > 0 ? (
          <div className="me-auto text-xs text-fg-muted">
            {`${t("Showing")} ${invoices.length} ${t("invoice(s)")}`}
          </div>
        ) : undefined
      }
    >
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Spinner size="glyph" className="!h-5 !w-5" />
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-12 px-5">
                    <PiReceiptLight className="h-8 w-8 mx-auto text-fg-muted mb-2" />
                    <p className="text-sm text-fg-muted">
                      {t("No invoices yet")}
                    </p>
                    <p className="text-xs text-fg-muted mt-1">
                      {t("Invoices will appear here after your first payment")}
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-hairline bg-surface-sunken/70">
                        <th className="text-start px-5 py-2.5 text-xs font-medium text-fg-muted">
                          {t("Date")}
                        </th>
                        <th className="text-start px-5 py-2.5 text-xs font-medium text-fg-muted">
                          {t("Invoice")}
                        </th>
                        <th className="text-start px-5 py-2.5 text-xs font-medium text-fg-muted">
                          {t("Amount")}
                        </th>
                        <th className="text-start px-5 py-2.5 text-xs font-medium text-fg-muted">
                          {t("Status")}
                        </th>
                        <th className="text-end px-5 py-2.5 text-xs font-medium text-fg-muted">
                          {t("Actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-hairline-soft">
                      {invoices.map((inv) => (
                        <tr
                          key={inv.id}
                          className="hover:bg-surface-sunken/70 transition-colors"
                        >
                          <td className="px-5 py-3 text-fg-strong whitespace-nowrap">
                            {formatDate(inv.created)}
                          </td>
                          <td className="px-5 py-3">
                            <p className="text-fg-strong font-medium">
                              {inv.number || "—"}
                            </p>
                            {inv.lines.length > 0 && (
                              <p className="text-xs text-fg-muted mt-0.5 truncate max-w-[200px]">
                                {inv.lines.map((l) => l.description).join(", ")}
                              </p>
                            )}
                          </td>
                          <td className="px-5 py-3 text-fg-strong whitespace-nowrap font-medium">
                            {formatCurrency(inv.amount_due, inv.currency)}
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(
                                inv.status
                              )}`}
                            >
                              {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-end whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              {inv.hosted_invoice_url && (
                                <a
                                  href={inv.hosted_invoice_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 rounded hover:bg-surface-sunken text-fg-muted hover:text-fg-strong transition-colors"
                                  title={t("View")}
                                >
                                  <PiArrowSquareOutLight className="h-4 w-4" />
                                </a>
                              )}
                              {inv.invoice_pdf && (
                                <a
                                  href={inv.invoice_pdf}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 rounded hover:bg-surface-sunken text-fg-muted hover:text-fg-strong transition-colors"
                                  title={t("Download PDF")}
                                >
                                  <PiDownloadSimpleLight className="h-4 w-4" />
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
    </ModalShell>
  );
};

export default InvoicesModal;
